import type RequestOptions from 'owa-service/lib/RequestOptions';
import type MailboxRequestOptions from 'owa-service/lib/MailboxRequestOptions';
import { getRequestOptionsOrDefaultV2 } from './getRequestOptionsOrDefaultV2';
import { getConnectedAccountRequestOptionsV2 } from './getConnectedAccountRequestOptionsV2';
import { isConnectedAccount, isMonarchMultipleAccountsEnabled } from 'owa-accounts-store';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { getExplicitLogonRequestOptionsV2 } from './getExplicitLogonRequestOptionsV2';
import { getArchiveMailboxRequestOptions } from './getArchiveMailboxRequestOptions';
import { getUserPrincipalRequestOptions } from './getUserPrincipalRequestOptions';
import { isSharedTypeMailbox } from 'owa-connected-account-headers';
import type { MailboxInfo } from 'owa-client-types';
import { isSameCoprincipalAccountMailboxInfos } from 'owa-client-types';
import { assertNever } from 'owa-assert';
import { isRunningOnWorker } from 'owa-config';
import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';

/**
 * getMailboxSpecificRequestOptions is used to intercept requests at owa-service and add
 * appropriate headers specific to Connected Accounts, Explicit logon etc.
 * @param options specifies MailboxRequestOptions which contains userIdentity (upn of logged in user), mailboxSmtpAddress (upn of mailbox opened) and mailboxType
 */
export default async function getMailboxSpecificRequestOptions(
    options: RequestOptions | undefined
): Promise<RequestOptions | undefined> {
    const mailboxRequestOptions = options as MailboxRequestOptions;
    const userIdentity = mailboxRequestOptions?.mailboxInfo?.userIdentity;

    if (
        !userIdentity ||
        options?.skipMailboxSpecificRequestOptions ||
        options?.preserveExplicitLogonRoutingHeaders
    ) {
        return options;
    }

    const requestType = getMailboxRequestType(mailboxRequestOptions?.mailboxInfo);
    switch (requestType) {
        case 'Default':
            return getRequestOptionsOrDefaultV2(options);
        case 'Connected':
            return getConnectedAccountRequestOptionsV2(mailboxRequestOptions);
        case 'Group':
            return getExplicitLogonRequestOptionsV2(
                mailboxRequestOptions,
                true /* useAnchorMailboxFromMailboxInfo */
            );
        case 'Shared':
            return getExplicitLogonRequestOptionsV2(mailboxRequestOptions);
        case 'Archive':
            return getArchiveMailboxRequestOptions(mailboxRequestOptions);
        case 'MonarchConnected':
            return getConnectedAccountRequestOptionsV2(mailboxRequestOptions);
        case 'UserPrincipal':
            return getUserPrincipalRequestOptions(mailboxRequestOptions);
        default:
            return assertNever(requestType);
    }
}

export type MailboxRequestType =
    | 'Default'
    | 'Connected'
    | 'Group'
    | 'Shared'
    | 'Archive'
    | 'MonarchConnected'
    | 'UserPrincipal';

function isUserPrincipal(mailboxInfo: MailboxInfo): boolean {
    return mailboxInfo.type === 'UserPrincipalName';
}

export function getMailboxRequestType(mailboxInfo: MailboxInfo): MailboxRequestType {
    // the worker MUST proxy this call back to main in worker initialize.ts because it
    // requires a fully functional accounts store
    if (isRunningOnWorker()) {
        throw new Error('Cannot compute mailbox request type on the worker!');
    }

    if (isUserPrincipal(mailboxInfo)) {
        return 'UserPrincipal';
    } else if (
        isGlobalFeatureEnabled('grp-centralizeRoutingHeaders') &&
        mailboxInfo.type === 'GroupMailbox'
    ) {
        return 'Group';
    } else if (isConnectedAccount(mailboxInfo.userIdentity)) {
        return 'Connected';
    } else if (isSharedTypeMailbox(mailboxInfo.type, mailboxInfo.mailboxSmtpAddress)) {
        return 'Shared';
    } else if (mailboxInfo.type == 'ArchiveMailbox') {
        return 'Archive';
    } else if (isMonarchMultipleAccountsEnabled() && !isGlobalAccount(mailboxInfo)) {
        return 'MonarchConnected';
    } else {
        return 'Default';
    }
}

function isGlobalAccount(mailboxInfo: MailboxInfo) {
    return isSameCoprincipalAccountMailboxInfos(getGlobalSettingsAccountMailboxInfo(), mailboxInfo);
}
